/**
 * Localización del contenido de `src/data/` (recetas, productos, noticias...).
 *
 * Los textos de interfaz viven en los JSON de `src/locales/` (i18next), pero el
 * contenido editorial se queda junto a cada elemento en `src/data/`, con un
 * objeto por idioma en cada campo traducible:
 *
 *   title: { es: 'Papas arrugadas', en: 'Wrinkled potatoes', fr: '...', pt: '...' }
 *
 * Así cada receta o producto sigue siendo una única entrada (id, imágenes,
 * slug y textos juntos) y se ve de un vistazo qué traducción falta. Los campos
 * que no se traducen (slugs, rutas de imagen, marcas, cifras) siguen siendo
 * valores simples y pasan tal cual.
 *
 * Módulo puro (sin React ni i18next) para poder testearlo aislado; el hook
 * `useLocalizeContent` lo enlaza con el idioma activo.
 */

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './languages';

/**
 * Indica si un valor es un texto traducible: un objeto plano cuyas claves son
 * todas códigos de idioma soportados y que incluye el idioma por defecto.
 * Un objeto normal (p. ej. `{ src, alt }`) no lo es.
 *
 * @param {*} value - Valor a comprobar
 * @returns {boolean} `true` si es un objeto `{ es, en?, fr?, pt? }`
 */
const isTranslatable = (value) => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return (
    keys.includes(DEFAULT_LANGUAGE) &&
    keys.every((key) => SUPPORTED_LANGUAGES.includes(key))
  );
};

/**
 * Indica si una traducción está vacía y debe usarse la del idioma por defecto.
 *
 * @param {*} value - Traducción de un idioma
 * @returns {boolean} `true` si falta o es una cadena/lista vacía
 */
const isMissing = (value) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/**
 * Resuelve un campo al idioma pedido, con el español como respaldo.
 *
 * @param {*} value - Campo de contenido: `{ es, en, fr, pt }` o un valor simple
 * @param {string} [lang] - Código de idioma activo
 * @returns {*} Texto (o lista) en ese idioma; los valores simples se devuelven igual
 */
export const localizeContent = (value, lang) => {
  if (!isTranslatable(value)) return value;
  const translated = value[lang];
  return isMissing(translated) ? value[DEFAULT_LANGUAGE] : translated;
};

/**
 * Resuelve recursivamente todos los campos traducibles de un elemento de
 * contenido (o de una lista de ellos). Devuelve una copia: el dato original
 * de `src/data/` no se modifica.
 *
 * @param {*} value - Elemento, lista o valor simple
 * @param {string} [lang] - Código de idioma activo
 * @returns {*} Copia con todos los textos en el idioma pedido
 */
export const localizeDeep = (value, lang) => {
  if (isTranslatable(value)) return localizeDeep(localizeContent(value, lang), lang);
  if (Array.isArray(value)) return value.map((item) => localizeDeep(item, lang));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, field]) => [key, localizeDeep(field, lang)])
    );
  }
  return value;
};
